/**
 * useAndroidBack — Register a back handler for the Android back button.
 *
 * Usage:
 *   useAndroidBack(BACK_PRIORITY.DIALOG, isOpen, () => { close(); return true })
 *
 * Only registers on Android native. No-op on web/iOS.
 *
 * @see docs/plans/view-and-dnd-master-plan.md §51
 */

import { useEffect } from "react"
import { Capacitor } from "@capacitor/core"
import { backHandlerStack } from "@/shared/utils/platform/backHandlerStack"

/**
 * Register an Android back handler when `enabled` is true.
 * Automatically unregisters when `enabled` becomes false or component unmounts.
 */
export function useAndroidBack(
  priority: number,
  enabled: boolean,
  handler: () => boolean | void,
  label?: string,
): void {
  useEffect(() => {
    if (!enabled) return
    if (!Capacitor.isNativePlatform()) return
    if (Capacitor.getPlatform() !== "android") return

    return backHandlerStack.register(priority, handler, label)
  }, [enabled, priority, handler, label])
}
